/**
 * <cevo-optin-form> – sends an opt-in to the Cloudflare Worker that syncs Omnisend. Used by the
 * reorder reminders page (sections/cevo-optin.liquid), the sign-up pop-up
 * (sections/cevo-signup-popup.liquid) and the sign-up band above the footer
 * (sections/cevo-signup-band.liquid).
 *
 * The Worker re-validates everything and checks the Cloudflare Turnstile token. The checks here
 * only exist to give the customer quick, specific feedback. Safe with several instances on one
 * page and with theme editor re-renders.
 *
 * Attributes: data-endpoint, data-source, data-turnstile-sitekey, and data-turnstile-defer, which
 * holds Turnstile back until prepareVerification() is called (the pop-up calls it on open). With
 * data-turnstile-defer="visible" it loads once the form comes near the screen or a field gets
 * focus (the band above the footer), so pages nobody scrolls to the bottom of never load it.
 * Fires a bubbling "cevo-optin:success" event after a sign-up.
 */
(() => {
  if (customElements.get('cevo-optin-form')) return;

  // Longer than the Worker's worst case (bot check plus two Omnisend calls, each retried once).
  const REQUEST_TIMEOUT_MS = 15000;
  const TOKEN_WAIT_MS = 10000;
  const TOKEN_POLL_MS = 200;
  const SENDING_LABEL = 'Sending…';
  const DEFAULT_SOURCE = 'keep-me-posted';
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  // Must match TURNSTILE_ACTION in the Worker (worker/src/turnstile.ts).
  const TURNSTILE_ACTION = 'turnstile-spin-v2';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE_E164_PATTERN = /^\+[1-9]\d{7,14}$/;
  // Turnstile's "flexible" widget needs 300px; narrower boxes (the pop-up on small phones) get "compact".
  const TURNSTILE_FLEXIBLE_MIN_WIDTH = 300;
  const SUCCESS_EVENT = 'cevo-optin:success';
  // How far below the screen the band may still be when Turnstile starts loading.
  const VISIBLE_DEFER_MARGIN = '600px 0px';

  const MESSAGES = Object.freeze({
    firstName: 'Please enter your first name.',
    email: 'Please enter a valid email address, like name@example.com.',
    phone: 'Please enter a mobile number, like 0412 345 678.',
    notConnected: 'Sign-ups are not switched on yet. Please try again later.',
    failed: 'That did not go through. Please try again in a moment.',
    verifying: 'Still checking that you are a person. If a tick box appears above the button, tick it and try again.',
    verificationFailed: 'We could not confirm that you are a person. Please try again.',
    verificationUnavailable: 'The security check did not load. Please refresh the page and try again.',
  });

  // First name is required only where its input says so (the page yes, the pop-up no).
  const FIELD_RULES = Object.freeze([
    { name: 'firstName', isValid: (values) => values.firstName.length > 0 || !values.isFirstNameRequired },
    { name: 'email', isValid: (values) => EMAIL_PATTERN.test(values.email) },
    { name: 'phone', isValid: (values) => values.phone === '' || PHONE_E164_PATTERN.test(values.phone) },
  ]);

  const isFieldName = (name) => FIELD_RULES.some((rule) => rule.name === name);

  /**
   * Australian mobiles typed any common way ("0412 345 678", "+61 412 345 678", "+61 0412…",
   * "0061…") become E.164, which is what Omnisend expects. Other input is returned compacted
   * and left for validation to judge.
   */
  function normalisePhone(raw) {
    const compact = raw.replace(/[\s().-]/g, '');
    if (/^04\d{8}$/.test(compact)) return `+61${compact.slice(1)}`;
    if (/^\+6104\d{8}$/.test(compact)) return `+61${compact.slice(4)}`;
    if (/^614\d{8}$/.test(compact)) return `+${compact}`;
    if (/^00[1-9]\d+$/.test(compact)) return `+${compact.slice(2)}`;
    return compact;
  }

  let turnstileLoader = null;

  /** Loads Cloudflare Turnstile once per page, however many forms there are. */
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileLoader) {
      turnstileLoader = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.addEventListener('load', () =>
          window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not initialise')),
        );
        script.addEventListener('error', () => reject(new Error('Turnstile script failed to load')));
        document.head.append(script);
      }).catch((error) => {
        turnstileLoader = null;
        throw error;
      });
    }
    return turnstileLoader;
  }

  class CevoOptinForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.formError = this.querySelector('[data-form-error]');
      this.successPanel = this.querySelector('[data-success]');
      this.turnstileBox = this.querySelector('[data-turnstile]');
      if (!this.form || !this.submitButton || !this.successPanel) return;

      this.idleLabel = this.submitButton.textContent.trim();
      this.isSubmitting = false;
      this.hasAttemptedSubmit = false;
      this.token = null;
      this.widgetId = null;
      this.turnstileRender = null;
      this.turnstileUnavailable = false;
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onInput);
      if (!this.hasAttribute('data-turnstile-defer')) {
        this.prepareVerification();
      } else if (this.dataset.turnstileDefer === 'visible') {
        this.prepareWhenNear();
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('input', this.onInput);
      this.stopWatchingVisibility();
      if (this.widgetId !== null) window.turnstile?.remove(this.widgetId);
      this.widgetId = null;
      this.turnstileRender = null;
    }

    usesTurnstile() {
      return Boolean(this.turnstileBox && this.dataset.turnstileSitekey);
    }

    /** Loads and renders Turnstile once. Called on connect, or by the pop-up when it opens. */
    prepareVerification() {
      if (!this.usesTurnstile()) return;
      this.turnstileRender ??= this.renderTurnstile();
    }

    /** data-turnstile-defer="visible": whichever comes first, nearly on screen or a field focused. */
    prepareWhenNear() {
      if (!this.usesTurnstile()) return;
      const prepare = () => {
        this.stopWatchingVisibility();
        this.prepareVerification();
      };
      this.form.addEventListener('focusin', prepare, { once: true });
      this.stopFocusWatch = () => this.form.removeEventListener('focusin', prepare);
      if (!('IntersectionObserver' in window)) return;
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) prepare();
        },
        { rootMargin: VISIBLE_DEFER_MARGIN },
      );
      this.visibilityObserver.observe(this);
    }

    stopWatchingVisibility() {
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      this.stopFocusWatch?.();
      this.stopFocusWatch = null;
    }

    async renderTurnstile() {
      try {
        const turnstile = await loadTurnstile();
        if (!this.isConnected || this.widgetId !== null) return;

        // Turnstile shows itself only when it needs a click ("interaction-only"). Its visibility is
        // left entirely to Cloudflare so a visitor asked to tick the box can always see it.
        this.widgetId = turnstile.render(this.turnstileBox, {
          sitekey: this.dataset.turnstileSitekey,
          action: TURNSTILE_ACTION,
          appearance: 'interaction-only',
          // Measured on the form: the Turnstile box itself is still empty, so Dawn hides it.
          size: this.form.clientWidth >= TURNSTILE_FLEXIBLE_MIN_WIDTH ? 'flexible' : 'compact',
          // Lets the pop-up take the box out of the layout while there is nothing to click.
          'before-interactive-callback': () => {
            this.turnstileBox.dataset.interactive = '';
          },
          'after-interactive-callback': () => {
            delete this.turnstileBox.dataset.interactive;
          },
          theme: 'light',
          callback: (token) => {
            this.token = token;
          },
          'expired-callback': () => {
            this.token = null;
          },
          'error-callback': () => {
            this.token = null;
          },
        });
      } catch (error) {
        this.turnstileUnavailable = true;
        console.error('[cevo-optin] Turnstile could not load:', error);
      }
    }

    /** Turnstile usually has a token before anyone finishes typing; wait briefly if not. */
    waitForToken() {
      return new Promise((resolve) => {
        const deadline = Date.now() + TOKEN_WAIT_MS;
        const poll = () => {
          if (this.token || this.turnstileUnavailable || Date.now() > deadline) {
            resolve(this.token);
            return;
          }
          setTimeout(poll, TOKEN_POLL_MS);
        };
        poll();
      });
    }

    /** Tokens are single-use, so every attempt that reached the Worker needs a fresh one. */
    resetTurnstile() {
      this.token = null;
      if (this.widgetId !== null) window.turnstile?.reset(this.widgetId);
    }

    onSubmit = async (event) => {
      event.preventDefault();
      if (this.isSubmitting) return;

      this.hasAttemptedSubmit = true;
      this.setFormError('');
      const values = this.readValues();
      const invalid = this.invalidFields(values);
      this.renderFieldErrors(invalid);
      if (invalid.length > 0) {
        this.focusField(invalid[0]);
        return;
      }

      // Bots fill the hidden field. They get the same thank-you and nothing is sent.
      if (values.honeypot) {
        this.showSuccess();
        return;
      }

      const endpoint = (this.dataset.endpoint || '').trim();
      if (!endpoint) {
        this.setFormError(MESSAGES.notConnected);
        return;
      }

      this.setSubmitting(true);
      try {
        await this.submit(endpoint, values);
      } catch (error) {
        console.error('[cevo-optin] Sign-up request failed:', error);
        this.resetTurnstile();
        this.setFormError(MESSAGES.failed);
      } finally {
        this.setSubmitting(false);
      }
    };

    async submit(endpoint, values) {
      this.prepareVerification();
      const token = this.usesTurnstile() ? await this.waitForToken() : '';
      if (this.usesTurnstile() && !token) {
        this.setFormError(this.turnstileUnavailable ? MESSAGES.verificationUnavailable : MESSAGES.verifying);
        return;
      }

      const result = await this.send(endpoint, values, token);
      if (result.ok) {
        this.showSuccess();
        return;
      }

      this.resetTurnstile();
      if (result.verificationFailed) {
        this.setFormError(MESSAGES.verificationFailed);
      } else if (result.field) {
        this.setFieldError(result.field, MESSAGES[result.field]);
        this.focusField(result.field);
      } else {
        this.setFormError(MESSAGES.failed);
      }
    }

    // Only clears errors, and only after a first submit, so nobody is told off mid-typing.
    onInput = (event) => {
      const { name } = event.target;
      if (!this.hasAttemptedSubmit || !isFieldName(name)) return;
      if (!this.invalidFields(this.readValues()).includes(name)) this.setFieldError(name, '');
    };

    readValues() {
      const data = new FormData(this.form);
      const text = (name) => String(data.get(name) ?? '').trim();
      return Object.freeze({
        firstName: text('firstName'),
        isFirstNameRequired: Boolean(this.form.elements.namedItem('firstName')?.required),
        email: text('email').toLowerCase(),
        phone: normalisePhone(text('phone')),
        remindersOnly: data.get('remindersOnly') === 'true',
        honeypot: text('cevo_check'),
      });
    }

    invalidFields(values) {
      return FIELD_RULES.filter((rule) => !rule.isValid(values)).map((rule) => rule.name);
    }

    async send(endpoint, values, turnstileToken) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            firstName: values.firstName,
            email: values.email,
            phone: values.phone,
            remindersOnly: values.remindersOnly,
            source: this.dataset.source || DEFAULT_SOURCE,
            turnstileToken,
          }),
          signal: controller.signal,
        });
        if (response.ok) return { ok: true, field: null, verificationFailed: false };

        const body = await response.json().catch(() => ({}));
        const field = isFieldName(body.field) ? body.field : null;
        const verificationFailed = body.code === 'verification_failed';
        if (!field && !verificationFailed) console.error(`[cevo-optin] Worker responded ${response.status}:`, body.error ?? '');
        return { ok: false, field, verificationFailed };
      } finally {
        clearTimeout(timeout);
      }
    }

    renderFieldErrors(invalidNames) {
      FIELD_RULES.forEach(({ name }) => {
        this.setFieldError(name, invalidNames.includes(name) ? MESSAGES[name] : '');
      });
    }

    setFieldError(name, message) {
      const input = this.form.elements.namedItem(name);
      const error = this.querySelector(`[data-error-for="${name}"]`);
      if (!input || !error) return;

      error.textContent = message;
      if (message) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }

    // Re-focusing a field that already has focus makes screen readers read it again, error included.
    focusField(name) {
      const field = this.form.elements.namedItem(name);
      if (!field) return;
      if (document.activeElement === field) field.blur();
      field.focus();
    }

    setFormError(message) {
      if (this.formError) this.formError.textContent = message;
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.submitButton.disabled = isSubmitting;
      this.submitButton.textContent = isSubmitting ? SENDING_LABEL : this.idleLabel;
      this.form.setAttribute('aria-busy', String(isSubmitting));
    }

    showSuccess() {
      this.form.hidden = true;
      this.successPanel.hidden = false;
      this.successPanel.focus();
      this.dispatchEvent(new CustomEvent(SUCCESS_EVENT, { bubbles: true }));
    }
  }

  customElements.define('cevo-optin-form', CevoOptinForm);
})();
